import { useMemo, useRef, useState, useEffect } from "react";
import { motion } from "motion/react";
import {
  Activity,
  HeartHandshake,
  ListChecks,
  MessageCircle,
  Search as SearchIcon,
  Smile,
  Sparkles,
  TreePine,
  X,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { ModuleId } from "@/pages/AppShell";

/* ─────────────────────  Indexable data  ───────────────────── */

type Category = "家人" | "訊息" | "清單" | "模組";

type Hit = {
  id: string;
  category: Category;
  title: string;
  subtitle?: string;
  initials?: string;
  color?: string;
  target: ModuleId;
};

const MEMBERS: Array<Hit> = [
  { id: "m-mom", category: "家人", title: "美儀", subtitle: "媽媽", initials: "美", color: "from-rose-400 to-rose-600", target: "famtree" },
  { id: "m-dad", category: "家人", title: "志強", subtitle: "爸爸", initials: "強", color: "from-amber-400 to-amber-600", target: "famtree" },
  { id: "m-gm", category: "家人", title: "秀蓮", subtitle: "嫲嫲", initials: "蓮", color: "from-violet-400 to-violet-600", target: "famtree" },
  { id: "m-sis", category: "家人", title: "小晴", subtitle: "家姐", initials: "晴", color: "from-emerald-400 to-emerald-600", target: "famtree" },
  { id: "m-you", category: "家人", title: "你", subtitle: "示範帳戶", initials: "你", color: "from-primary to-primary/70", target: "today" },
];

const MESSAGES: Array<Hit> = [
  { id: "c1", category: "訊息", title: "今晚食飯記得早啲返嚟呀 ❤️", subtitle: "媽媽 · 18:02", target: "famchat" },
  { id: "c2", category: "訊息", title: "收到！我 6 點收工，會趕到返屋企", subtitle: "你 · 18:04", target: "famchat" },
  { id: "c3", category: "訊息", title: "我煮咗糖水，記得留肚返嚟 🥣", subtitle: "嫲嫲 · 18:05", target: "famchat" },
  { id: "c4", category: "訊息", title: "街市買咗你哋最鍾意嘅菜，今晚我嚟煮！", subtitle: "爸爸 · 18:08", target: "famchat" },
  { id: "c5", category: "訊息", title: "嫲嫲你把聲好精神！等等我帶蛋糕返嚟 🎂", subtitle: "你 · 18:12", target: "famchat" },
];

const LIST_ITEMS: Array<Hit> = [
  { id: "l1", category: "清單", title: "今晚煮飯（嫲嫲話要糖水）", subtitle: "家庭事項 · 今天 18:00", target: "famlist" },
  { id: "l2", category: "清單", title: "交 5 月電費", subtitle: "家庭事項 · 週五", target: "famlist" },
  { id: "l3", category: "清單", title: "嫲嫲藥物補充", subtitle: "家庭事項 · 每週", target: "famlist" },
  { id: "l4", category: "清單", title: "帶媽媽去屯門醫院覆診", subtitle: "媽媽覆診 · 明天 10:00", target: "famlist" },
  { id: "l5", category: "清單", title: "買米 5kg + 紙巾", subtitle: "超市購物", target: "famlist" },
  { id: "l6", category: "清單", title: "嫲嫲嘅低鈉豉油", subtitle: "超市購物", target: "famlist" },
  { id: "l7", category: "清單", title: "簽通告 · 旅行日", subtitle: "小晴返學 · 週三", target: "famlist" },
];


const MODULE_HITS: Array<{ id: string; title: string; icon: LucideIcon; target: ModuleId; locked?: boolean }> = [
  { id: "mod-today", title: "今日", icon: Sparkles, target: "today" },
  { id: "mod-status", title: "FamStatus · 打卡", icon: Activity, target: "famstatus" },
  { id: "mod-list", title: "FamList · 清單", icon: ListChecks, target: "famlist" },
  { id: "mod-chat", title: "FamChat · 家人聊天室", icon: MessageCircle, target: "famchat" },
  { id: "mod-tree", title: "FamTree · 家族樹", icon: TreePine, target: "famtree" },
  { id: "mod-match", title: "FamMatch · 配對", icon: HeartHandshake, target: "fammatch" },
];

const ALL_HITS: Hit[] = [...MEMBERS, ...MESSAGES, ...LIST_ITEMS];

/* Display order for grouped results — also drives the initial
 * `grouped` buckets and the `totalHits` sum so adding a new
 * Category only needs one edit here.
 */
const CATEGORIES: readonly Category[] = ["家人", "訊息", "清單", "模組"];

const RECENT_SEARCHES = ["嫲嫲", "覆診", "期待", "煮飯"];

/* ─────────────────────  View  ───────────────────── */

type Props = {
  onNavigate: (id: ModuleId) => void;
  onClose: () => void;
};

export function SearchPanel({ onNavigate, onClose }: Props) {
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmed = q.trim().toLowerCase();
  const hasQuery = trimmed.length > 0;

  const navigateAndClose = (id: ModuleId): void => {
    onNavigate(id);
    onClose();
  };

  const grouped = useMemo(() => {
    const out = Object.fromEntries(
      CATEGORIES.map((c) => [c, [] as Hit[]]),
    ) as Record<Category, Hit[]>;
    if (!hasQuery) return out;
    for (const h of ALL_HITS) {
      if (
        h.title.toLowerCase().includes(trimmed) ||
        h.subtitle?.toLowerCase().includes(trimmed)
      ) {
        out[h.category].push(h);
      }
    }
    for (const m of MODULE_HITS) {
      if (m.title.toLowerCase().includes(trimmed)) {
        out["模組"].push({
          id: m.id,
          category: "模組",
          title: m.title,
          target: m.target,
        });
      }
    }
    return out;
  }, [trimmed, hasQuery]);

  const totalHits = useMemo(
    () => CATEGORIES.reduce((sum, c) => sum + grouped[c].length, 0),
    [grouped],
  );

  return (
    <div>
      <div className="relative">
        <SearchIcon
          className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onClose();
          }}
          placeholder="搜尋家人、訊息、清單…"
          className="h-11 w-full rounded-full border border-border/60 bg-background pl-10 pr-10 text-sm placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
        />
        {q && (
          <button
            type="button"
            onClick={() => {
              setQ("");
              inputRef.current?.focus();
            }}
            aria-label="清除搜尋"
            className="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-foreground/60 hover:bg-secondary"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>

      {!hasQuery ? (
        <div className="mt-5 space-y-4">
          <div>
            <div className="px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              快速前往
            </div>
            <ul className="mt-2 grid grid-cols-4 gap-2">
              {MODULE_HITS.map((m) => {
                const Icon = m.icon;
                return (
                  <li key={m.id}>
                    <motion.button
                      type="button"
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigateAndClose(m.target)}
                      className={cn(
                        "flex w-full flex-col items-center gap-1.5 rounded-2xl border border-border/40 bg-card p-2.5 text-[11px] font-medium transition-colors hover:bg-secondary",
                        m.locked && "opacity-60",
                      )}
                    >
                      <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/10 text-primary">
                        <Icon className="h-4.5 w-4.5" aria-hidden />
                      </span>
                      <span className="truncate">{m.title}</span>
                    </motion.button>
                  </li>
                );
              })}
            </ul>
          </div>
          <div>
            <div className="px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              最近搜尋
            </div>
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {RECENT_SEARCHES.map((term) => (
                <li key={term}>
                  <button
                    type="button"
                    onClick={() => setQ(term)}
                    className="inline-flex h-8 items-center rounded-full border border-border/60 bg-card px-3 text-xs text-foreground/80 hover:bg-secondary"
                  >
                    {term}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : totalHits === 0 ? (
        <div className="mt-6 flex flex-col items-center gap-2 py-6 text-center">
          <span
            aria-hidden
            className="grid h-12 w-12 place-items-center rounded-2xl bg-secondary text-foreground/60"
          >
            <SearchIcon className="h-5 w-5" aria-hidden />
          </span>
          <div className="text-sm font-semibold">搵唔到「{q}」嘅結果</div>
          <p className="text-xs text-muted-foreground">
            試下其他關鍵字，或者直接點上面嘅模組。
          </p>
        </div>
      ) : (
        <ul className="mt-3 space-y-3">
          {CATEGORIES.map((cat) =>
            grouped[cat].length > 0 ? (
              <li key={cat}>
                <div className="px-1 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {cat} · {grouped[cat].length}
                </div>
                <ul className="space-y-1.5">
                  {grouped[cat].map((h) => (
                    <li key={h.id}>
                      <motion.button
                        type="button"
                        whileTap={{ scale: 0.98 }}
                        onClick={() => navigateAndClose(h.target)}
                        className="flex w-full items-center gap-3 rounded-2xl border border-border/40 bg-card p-2.5 text-left transition-colors hover:bg-secondary"
                      >
                        {h.initials ? (
                          <span
                            aria-hidden
                            className={cn(
                              "grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br text-xs font-semibold text-white",
                              h.color,
                            )}
                          >
                            {h.initials}
                          </span>
                        ) : (
                          <span
                            aria-hidden
                            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"
                          >
                            <Sparkles className="h-4 w-4" aria-hidden />
                          </span>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">
                            {h.title}
                          </div>
                          {h.subtitle && (
                            <div className="truncate text-[11px] text-muted-foreground">
                              {h.subtitle}
                            </div>
                          )}
                        </div>
                      </motion.button>
                    </li>
                  ))}
                </ul>
              </li>
            ) : null,
          )}
        </ul>
      )}
    </div>
  );
}
